import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Typography,
} from '@mui/material';
import { DataGrid, type GridColDef, type GridRenderCellParams } from '@mui/x-data-grid';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { useQuery } from '@tanstack/react-query';
import { fetchSecurityAudit, type AuditCheckResult, type CheckStatus } from '../api/security';
import { usePermission } from '../hooks/usePermission';

const STATUS_COLOR: Record<CheckStatus, 'success' | 'warning' | 'error'> = {
  pass: 'success',
  warn: 'warning',
  fail: 'error',
};

const columns: GridColDef<AuditCheckResult>[] = [
  {
    field: 'status',
    headerName: 'Status',
    width: 90,
    renderCell: (p: GridRenderCellParams<AuditCheckResult, CheckStatus>) => (
      <Chip
        label={p.value}
        size="small"
        color={STATUS_COLOR[p.value!]}
      />
    ),
  },
  { field: 'title', headerName: 'Check', flex: 1, minWidth: 160 },
  {
    field: 'detail',
    headerName: 'Detail',
    flex: 2,
    minWidth: 200,
    renderCell: (p: GridRenderCellParams<AuditCheckResult, string>) => (
      <Box sx={{ whiteSpace: 'normal', wordBreak: 'break-word', py: 0.5, lineHeight: 1.4 }}>
        {p.value}
      </Box>
    ),
  },
];

function groupByCategory(results: AuditCheckResult[]): Map<string, AuditCheckResult[]> {
  const map = new Map<string, AuditCheckResult[]>();
  for (const item of results) {
    const list = map.get(item.category) ?? [];
    list.push(item);
    map.set(item.category, list);
  }
  return map;
}

export function SecurityAudit() {
  const canAudit = usePermission('security.audit');
  const [triggered, setTriggered] = useState(false);

  const { data, isFetching, error, refetch, dataUpdatedAt } = useQuery({
    queryKey: ['security-audit'],
    queryFn: fetchSecurityAudit,
    enabled: false,
    staleTime: Infinity,
    retry: false,
  });

  async function handleRun() {
    setTriggered(true);
    await refetch();
  }

  if (!canAudit) {
    return <Typography color="error">You do not have permission to run security audits.</Typography>;
  }

  const grouped = data ? groupByCategory(data) : null;

  return (
    <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
        <Typography variant="h5">Security Audit</Typography>
        <Button
          variant="contained"
          startIcon={isFetching ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
          disabled={isFetching}
          onClick={() => void handleRun()}
        >
          Run Audit
        </Button>
      </Box>

      {dataUpdatedAt > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 3 }}>
          Last run: {new Date(dataUpdatedAt).toLocaleString()}
        </Typography>
      )}

      {!triggered && (
        <Typography color="text.secondary" sx={{ mt: 4, textAlign: 'center' }}>
          No results yet — press Run Audit to scan.
        </Typography>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error instanceof Error ? error.message : 'Audit failed'}
        </Alert>
      )}

      {grouped && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {[...grouped.entries()].map(([category, rows]) => (
            <Box key={category}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 700 }}>
                {category}
              </Typography>
              <DataGrid
                rows={rows}
                columns={columns}
                getRowId={(row) => row.id}
                density="compact"
                disableRowSelectionOnClick
                hideFooter
                autoHeight
                getRowHeight={() => 'auto'}
                sx={{
                  bgcolor: 'background.paper',
                  '& .MuiDataGrid-cell': { alignItems: 'flex-start' },
                }}
              />
            </Box>
          ))}
        </Box>
      )}

      {isFetching && !data && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          <CircularProgress />
        </Box>
      )}
    </Box>
  );
}
